/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module AviFormsModule
 */

/**
 * @description Parses and formats comma-separated string of IP addresses.
 *
 * @author Aravindh Nagarajan
 */

import {
    Directive,
    ElementRef,
    forwardRef,
    HostListener,
    Provider,
    Renderer2,
} from '@angular/core';

import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';

import { IIpAddr } from 'generated-types';

import {
    formatIpAddrList,
    parseIpList,
} from 'ng/shared/utils/ip-parser.utils';

import { REGEX_PATTERN_VALIDATOR_KEY } from 'ng/modules/avi-forms/validators/regex-pattern';

const PARSE_IP_LIST_DIRECTIVE_ACCESSOR: Provider = {
    multi: true,
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => ParseIpListDirective),
};

@Directive({
    selector: '[parseIpList]',
    providers: [
        PARSE_IP_LIST_DIRECTIVE_ACCESSOR,
        {
            multi: true,
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ParseIpListDirective),
        },
    ],
})
export class ParseIpListDirective implements ControlValueAccessor, Validator {
    public constructor(
        private renderer: Renderer2,
        private elementRef: ElementRef,
    ) {}

    /**
     * Listener for the input change event.
     * parses Input value and calls onChange to proceed with the new model value.
     */
    @HostListener('input', ['$event.target.value'])
    private onInputChange(value: string): void {
        const modelValue: IIpAddr[] | null = this.parse(value);

        this.onChange(modelValue);
    }

    /**
     * Writes the input display value with ipAddresses string.
     */
    public writeValue(value: IIpAddr[]): void {
        const { nativeElement: hostElement } = this.elementRef;
        const formattedIp = this.format(value);

        this.renderer.setProperty(hostElement, 'value', formattedIp);
    }

    /**
     * Sets the onChange function.
     */
    public registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    /**
     * Sets the onTouched function.
     */
    public registerOnTouched(fn: any): void {
        this.onTouch = fn;
    }

    /**
     * @override
     */
    public validate(control: AbstractControl): ValidationErrors | null {
        /**
         * If any addresses are invalid, parseIpList returns null.
         */
        if (control.value === null) {
            return {
                [REGEX_PATTERN_VALIDATOR_KEY]: true,
            };
        }

        return null;
    }

    /**
     * Method to be overridden by the ControlValueAccessor interface.
     *
     * This method will be used to update ngModel value when user
     * changes input value.
     */
    private onChange: (value: string | IIpAddr[]) => void = (value: string) => {};

    /**
     * Method to be overridden by the ControlValueAccessor interface.
     */
    private onTouch: () => void = () => {};

    /**
     * Formats IpAddr objects list into comma separated IP address string.
     */
    private format(val: IIpAddr[]): string {
        return formatIpAddrList(val);
    }

    /**
     * Parses IP addresses string into a list of IPAddr object.
     */
    private parse(val: string): IIpAddr[] {
        return parseIpList(val);
    }
}
